import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Users, ThumbsUp, Clock, Plus, X, Music, Upload, Smile, Pencil, Trash2, Check } from 'lucide-react';
import EmotePicker from '../EmotePicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const REACTION_EMOJIS = ["❤️", "👍", "😂", "🔥", "🎉", "👏", "💯", "✨"];

const ProducerForumTab = ({ user, onNewPostClick }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('qa');
  const [showNewPostModal, setShowNewPostModal] = useState(false);
  const [newPostTitle, setNewPostTitle] = useState('');
  const [newPostContent, setNewPostContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showEmotePicker, setShowEmotePicker] = useState(false);
  const [editingPostId, setEditingPostId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [reactionPickerPostId, setReactionPickerPostId] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [postComments, setPostComments] = useState([]);
  const [commentContent, setCommentContent] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentContent, setEditCommentContent] = useState('');
  const [reactionPickerCommentId, setReactionPickerCommentId] = useState(null);
  const [detailViewMode, setDetailViewMode] = useState('post');
  
  // Track Preview state (for feedback category)
  const [showAudioUpload, setShowAudioUpload] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const [clipStartTime, setClipStartTime] = useState(0);
  const [clipEndTime, setClipEndTime] = useState(45);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [audioUploading, setAudioUploading] = useState(false);
  const [audioUploadId, setAudioUploadId] = useState(null);
  const [userQuota, setUserQuota] = useState({ current: 0, max: 2 });
  const audioPlayerRef = useRef(null);

  const forumCategories = [
    { id: 'qa', name: 'Q&A', icon: '❓', description: 'Ask questions, get answers' },
    { id: 'stories', name: 'Success Stories', icon: '🌟', description: 'Share your journey' },
    { id: 'feedback', name: 'Track Feedback', icon: '🎵', description: 'Get feedback on your tracks' }
  ];

  useEffect(() => {
    loadPosts();
  }, [activeCategory]);

  useEffect(() => {
    if (activeCategory === 'feedback') {
      loadUserQuota();
    }
  }, [activeCategory]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/forum/posts?category=${activeCategory}`);
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Error loading forum posts:', error);
      setPosts([
        { id: 1, title: 'How to get that punchy kick?', author_name: 'NewProducer', replies_count: 12, likes_count: 24, created_at: '2 hours ago' },
        { id: 2, title: 'Best VST for ambient textures?', author_name: 'SynthWizard', replies_count: 8, likes_count: 15, created_at: '4 hours ago' },
        { id: 3, title: 'Mixing low end tips needed', author_name: 'BassHead', replies_count: 21, likes_count: 45, created_at: '1 day ago' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadUserQuota = async () => {
    try {
      const token = localStorage.getItem('beatseek_token');
      if (!token) return;
      
      const response = await fetch(`${BACKEND_URL}/api/forum/audio/quota`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUserQuota({ current: data.active_count || 0, max: data.max_allowed || 2 });
      }
    } catch (error) {
      console.error('Error loading quota:', error);
    }
  };

  const handleNewPost = () => {
    const token = localStorage.getItem('beatseek_token');
    if (!token) {
      alert('Please login to create a new post');
      return;
    }
    setShowNewPostModal(true);
  };

  const handleAudioFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      alert('File size too large. Maximum size is 10MB.');
      return;
    }

    const allowedTypes = ['audio/mp3', 'audio/mpeg', 'audio/wav', 'audio/m4a', 'audio/aac', 'audio/x-m4a'];
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|m4a|aac)$/i)) {
      alert('Invalid file type. Please upload MP3, WAV, M4A, or AAC files.');
      return;
    }

    setAudioFile(file);
    const url = URL.createObjectURL(file);
    setAudioPreviewUrl(url);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const playClipPreview = () => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.currentTime = clipStartTime;
      audioPlayerRef.current.play();
      setTimeout(() => {
        if (audioPlayerRef.current) {
          audioPlayerRef.current.pause();
        }
      }, 45000);
    }
  };

  const handleAudioUpload = async () => {
    if (!audioFile || !termsAccepted) return;

    if (userQuota.current >= userQuota.max) {
      alert(`You have reached the maximum of ${userQuota.max} active track previews. Please wait for existing tracks to expire.`);
      return;
    }

    setAudioUploading(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const formData = new FormData();
      formData.append('file', audioFile);

      const response = await fetch(`${BACKEND_URL}/api/forum/audio/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setAudioUploadId(data.id);
        
        // Set clip time if needed
        if (clipStartTime > 0) {
          await fetch(`${BACKEND_URL}/api/forum/audio/${data.id}/clip`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              clip_start_time: clipStartTime,
              clip_duration: 45
            })
          });
        }
        
        setShowAudioUpload(false);
        alert('Track preview added successfully!');
      } else {
        const error = await response.json();
        alert(`Upload failed: ${error.detail || 'Please try again'}`);
      }
    } catch (error) {
      console.error('Error uploading audio:', error);
      alert('Error uploading audio. Please try again.');
    } finally {
      setAudioUploading(false);
    }
  };

  const submitNewPost = async () => {
    if (!newPostTitle.trim() || !newPostContent.trim()) {
      alert('Please fill in both title and content');
      return;
    }

    // For feedback category, require track preview
    if (activeCategory === 'feedback' && !audioUploadId) {
      alert('Please add a track preview for the feedback category');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/forum/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: newPostTitle.trim(),
          content: newPostContent.trim(),
          category: activeCategory,
          audio_preview_id: audioUploadId || null
        })
      });

      if (response.ok) {
        const newPost = await response.json();
        
        if (newPost.moderation_status === 'pending_moderation') {
          alert('⏳ Your post has been sent to admin for approval\n\nOur AI detected potentially inappropriate content. An admin will review your post shortly.');
        } else {
          setPosts(prevPosts => [newPost, ...prevPosts]);
          alert('Post created successfully!');
        }
        
        resetForm();
      } else {
        const errorData = await response.json();
        alert(`Failed to create post: ${errorData.detail || 'Please try again'}`);
      }
    } catch (error) {
      console.error('Error creating post:', error);
      alert('Error creating post. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setShowNewPostModal(false);
    setNewPostTitle('');
    setNewPostContent('');
    setAudioFile(null);
    setAudioPreviewUrl(null);
    setAudioUploadId(null);
    setClipStartTime(0);
    setClipEndTime(45);
    setTermsAccepted(false);
    setAudioDuration(0);
    setShowEmotePicker(false);
  };

  const handleEmoteSelect = (emoteCode) => {
    setNewPostContent(prev => prev + emoteCode);
    setShowEmotePicker(false);
  };

  // Handle delete post
  const handleDeletePost = async (postId, event) => {
    if (event) event.stopPropagation();
    
    const token = localStorage.getItem('beatseek_token');
    if (!token) { alert('Please login'); return; }
    if (!window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${postId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        loadPosts();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete post');
      }
    } catch (error) {
      console.error('Failed to delete post:', error);
    }
  };

  // Handle edit post
  const handleEditPost = async (postId, event) => {
    if (event) event.stopPropagation();
    
    const token = localStorage.getItem('beatseek_token');
    if (!token) return;

    try {
      const post = posts.find(p => p.id === postId);
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${postId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: editTitle, content: editContent, category: post?.category || activeCategory })
      });
      if (response.ok) {
        setEditingPostId(null);
        setEditTitle('');
        setEditContent('');
        loadPosts();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to edit post');
      }
    } catch (error) {
      console.error('Failed to edit post:', error);
    }
  };

  const startEditing = (post, event) => {
    if (event) event.stopPropagation();
    setEditingPostId(post.id);
    setEditTitle(post.title);
    setEditContent(post.content || '');
  };

  const cancelEditing = (event) => {
    if (event) event.stopPropagation();
    setEditingPostId(null);
    setEditTitle('');
    setEditContent('');
  };

  // Handle reaction
  const handleReaction = async (postId, emoji, event) => {
    if (event) event.stopPropagation();
    
    const token = localStorage.getItem('beatseek_token');
    if (!token) { alert('Please login to react'); return; }

    try {
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${postId}/reactions`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ emoji })
      });
      if (response.ok) {
        setReactionPickerPostId(null);
        loadPosts();
      }
    } catch (error) {
      console.error('Failed to react:', error);
    }
  };

  // Load comments for a post
  const loadPostComments = async (postId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${postId}/replies`);
      if (response.ok) {
        const data = await response.json();
        setPostComments(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to load comments:', error);
    }
  };

  // Post a comment
  const postComment = async () => {
    if (!commentContent || !selectedPost) return;
    
    const token = localStorage.getItem('beatseek_token');
    if (!token) { alert('Please login to comment'); return; }

    try {
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${selectedPost.id}/replies`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: commentContent })
      });
      if (response.ok) {
        setCommentContent('');
        loadPostComments(selectedPost.id);
        loadPosts();
      }
    } catch (error) {
      console.error('Failed to post comment:', error);
    }
  };

  // Delete comment
  const handleDeleteComment = async (commentId) => {
    const token = localStorage.getItem('beatseek_token');
    if (!token) return;
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${selectedPost.id}/replies/${commentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) { loadPostComments(selectedPost.id); loadPosts(); }
      else { const err = await response.json(); alert(err.detail || 'Failed to delete comment'); }
    } catch (error) { console.error('Failed to delete comment:', error); }
  };

  // Edit comment
  const handleEditComment = async (commentId) => {
    const token = localStorage.getItem('beatseek_token');
    if (!token || !editCommentContent.trim()) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${selectedPost.id}/replies/${commentId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: editCommentContent })
      });
      if (response.ok) { setEditingCommentId(null); setEditCommentContent(''); loadPostComments(selectedPost.id); }
      else { const err = await response.json(); alert(err.detail || 'Failed to edit comment'); }
    } catch (error) { console.error('Failed to edit comment:', error); }
  };

  // React to comment
  const handleCommentReaction = async (commentId, emoji) => {
    const token = localStorage.getItem('beatseek_token');
    if (!token) { alert('Please login to react'); return; }

    try {
      const response = await fetch(`${BACKEND_URL}/api/forum/replies/${commentId}/reactions`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ emoji })
      });
      if (response.ok) { setReactionPickerCommentId(null); loadPostComments(selectedPost.id); }
    } catch (error) { console.error('Failed to react to comment:', error); }
  };

  // Time ago helper
  const getTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const getCategoryInfo = () => {
    const cat = forumCategories.find(c => c.id === activeCategory);
    return {
      name: cat?.name || 'Forum',
      titlePlaceholder: activeCategory === 'qa' ? 'What\'s your question?' :
                       activeCategory === 'stories' ? 'Your journey title...' :
                       'Track name or feedback request...',
      contentPlaceholder: activeCategory === 'qa' ? 'Ask your production question...' :
                         activeCategory === 'stories' ? 'Share your producer journey...' :
                         'Describe your track and what kind of feedback you\'re looking for...'
    };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    if (typeof dateStr === 'string' && !dateStr.includes('T')) return dateStr;
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffHours < 1) return 'Just now';
      if (diffHours < 24) return `${diffHours} hours ago`;
      if (diffDays < 7) return `${diffDays} days ago`;
      return date.toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  // Post Detail View (with comments)
  if (selectedPost) {
    return (
      <div className="producer-forum-tab" style={{ padding: '0' }}>
        {/* Back button + header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
          <button
            data-testid="back-to-posts"
            onClick={() => { setSelectedPost(null); setPostComments([]); setCommentContent(''); setDetailViewMode('post'); }}
            style={{
              background: 'rgba(147, 51, 234, 0.1)',
              border: '1px solid rgba(147, 51, 234, 0.3)',
              color: '#9333ea',
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer',
              borderRadius: '0.5rem',
              transition: 'all 0.3s ease'
            }}
          >
            ← Back to Posts
          </button>

          {/* Author-only: Edit & Delete */}
          {(() => {
            const currentUser = JSON.parse(localStorage.getItem('beatseek_user') || '{}');
            const isAuthor = currentUser.id === selectedPost.author_id || currentUser.user_id === selectedPost.author_id;
            return isAuthor ? (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => { setEditingPostId(selectedPost.id); setEditTitle(selectedPost.title); setEditContent(selectedPost.content || ''); }}
                  style={{
                    background: 'rgba(147, 51, 234, 0.1)',
                    border: '1px solid rgba(147, 51, 234, 0.3)',
                    color: '#9333ea',
                    padding: '0.5rem 1rem',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    borderRadius: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem'
                  }}
                >
                  <Pencil size={14} /> Edit
                </button>
                <button
                  onClick={() => handleDeletePost(selectedPost.id)}
                  style={{
                    background: 'rgba(255, 0, 0, 0.1)',
                    border: '1px solid rgba(255, 0, 0, 0.3)',
                    color: '#ff4444',
                    padding: '0.5rem 1rem',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    borderRadius: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem'
                  }}
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            ) : null;
          })()}
        </div>

        {/* Mode toggle tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <button
            data-testid="mode-view-post"
            onClick={() => setDetailViewMode('post')}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              border: detailViewMode === 'post' ? '1px solid rgba(147, 51, 234, 0.6)' : '1px solid rgba(255, 255, 255, 0.15)',
              background: detailViewMode === 'post' ? 'rgba(147, 51, 234, 0.15)' : 'transparent',
              color: detailViewMode === 'post' ? '#9333ea' : 'rgba(255, 255, 255, 0.5)',
              fontSize: '0.8rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            View Post
          </button>
          <button
            data-testid="mode-view-comments"
            onClick={() => setDetailViewMode('comments')}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              border: detailViewMode === 'comments' ? '1px solid rgba(251, 191, 36, 0.6)' : '1px solid rgba(255, 255, 255, 0.15)',
              background: detailViewMode === 'comments' ? 'rgba(251, 191, 36, 0.15)' : 'transparent',
              color: detailViewMode === 'comments' ? '#fbbf24' : 'rgba(255, 255, 255, 0.5)',
              fontSize: '0.8rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Comments ({postComments.length})
          </button>
        </div>

        {detailViewMode === 'post' && (
        <>
        {/* Post content card */}
        <div style={{
          background: 'rgba(30, 20, 50, 0.5)',
          border: '1px solid rgba(147, 51, 234, 0.3)',
          borderRadius: '0.75rem',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.75rem' }}>
              by {selectedPost.author_name || selectedPost.author} • {getTimeAgo(selectedPost.created_at)}
            </span>
          </div>

          {editingPostId === selectedPost.id ? (
            <div style={{ marginBottom: '1rem' }}>
              <input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(147,51,234,0.5)', borderRadius: '0.4rem', color: '#fff', fontSize: '1rem', fontWeight: '600', outline: 'none' }}
              />
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                rows={4}
                style={{ width: '100%', padding: '0.5rem', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(147,51,234,0.5)', borderRadius: '0.4rem', color: '#fff', fontSize: '0.85rem', outline: 'none', resize: 'vertical' }}
              />
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                <button onClick={(e) => handleEditPost(selectedPost.id, e)} style={{ background: 'rgba(0,255,0,0.2)', border: '1px solid rgba(0,255,0,0.5)', borderRadius: '0.4rem', color: '#00ff00', padding: '0.3rem 0.75rem', fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}><Check size={12} /> Save</button>
                <button onClick={(e) => cancelEditing(e)} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '0.4rem', color: 'rgba(255,255,255,0.7)', padding: '0.3rem 0.75rem', fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}><X size={12} /> Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#fff', marginBottom: '1rem' }}>
                {selectedPost.title}
              </h2>
              <p style={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: '1.6', marginBottom: '1rem', whiteSpace: 'pre-wrap' }}>
                {selectedPost.content}
              </p>
            </>
          )}
        </div>

        {/* Comment input */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#fff', marginBottom: '0.75rem' }}>
            Add a Comment
          </h3>
          <textarea
            data-testid="comment-input"
            value={commentContent}
            onChange={(e) => setCommentContent(e.target.value)}
            placeholder="Add a comment..."
            style={{
              width: '100%',
              minHeight: '100px',
              padding: '0.75rem',
              background: 'rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(147, 51, 234, 0.3)',
              borderRadius: '0.5rem',
              color: '#fff',
              fontSize: '0.875rem',
              outline: 'none',
              resize: 'vertical'
            }}
          />
          <button
            data-testid="post-comment-btn"
            onClick={postComment}
            style={{
              marginTop: '0.5rem',
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.3), rgba(251, 191, 36, 0.2))',
              border: '1px solid rgba(147, 51, 234, 0.5)',
              borderRadius: '0.5rem',
              color: '#fff',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Post Comment
          </button>
        </div>
        </>
        )}

        {detailViewMode === 'comments' && (
        <>
        {/* Comments section */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#fff', marginBottom: '1rem' }}>
            Comments ({postComments.length})
          </h3>

          {/* Comments list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {postComments.map(comment => (
              <div
                key={comment.id}
                style={{
                  background: 'rgba(0, 0, 0, 0.2)',
                  border: '1px solid rgba(147, 51, 234, 0.15)',
                  borderRadius: '0.5rem',
                  padding: '1rem'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: '#9333ea', fontSize: '0.875rem', fontWeight: '600' }}>
                    {comment.author_name}
                  </span>
                  <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.75rem' }}>
                    {getTimeAgo(comment.created_at)}
                  </span>
                </div>

                {editingCommentId === comment.id ? (
                  <div>
                    <textarea
                      value={editCommentContent}
                      onChange={(e) => setEditCommentContent(e.target.value)}
                      rows={2}
                      style={{ width: '100%', padding: '0.5rem', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(147,51,234,0.5)', borderRadius: '0.4rem', color: '#fff', fontSize: '0.85rem', outline: 'none', resize: 'vertical' }}
                    />
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.4rem' }}>
                      <button onClick={() => handleEditComment(comment.id)} style={{ background: 'rgba(0,255,0,0.2)', border: '1px solid rgba(0,255,0,0.5)', borderRadius: '0.4rem', color: '#00ff00', padding: '0.2rem 0.6rem', fontSize: '0.7rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.2rem' }}><Check size={11} /> Save</button>
                      <button onClick={() => { setEditingCommentId(null); setEditCommentContent(''); }} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '0.4rem', color: 'rgba(255,255,255,0.7)', padding: '0.2rem 0.6rem', fontSize: '0.7rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.2rem' }}><X size={11} /> Cancel</button>
                    </div>
                  </div>
                ) : (
                  <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.875rem', lineHeight: '1.5' }}>
                    {comment.content}
                  </p>
                )}

                {/* Reactions display */}
                {comment.reactions && comment.reactions.length > 0 && (
                  <div style={{ display: 'flex', gap: '0.3rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                    {comment.reactions.map((r, rIdx) => (
                      <span key={rIdx} onClick={() => handleCommentReaction(comment.id, r.emoji)} style={{ background: 'rgba(147,51,234,0.15)', border: '1px solid rgba(147,51,234,0.3)', borderRadius: '1rem', padding: '0.1rem 0.4rem', fontSize: '0.7rem', cursor: 'pointer' }}>{r.emoji} {r.count}</span>
                    ))}
                  </div>
                )}

                {/* Comment actions row */}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', alignItems: 'center' }}>
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={() => setReactionPickerCommentId(reactionPickerCommentId === comment.id ? null : comment.id)}
                      style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.4)', padding: '0.15rem 0.35rem', borderRadius: '0.3rem', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'all 0.2s' }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = '#9333ea'; e.currentTarget.style.borderColor = 'rgba(147,51,234,0.4)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; }}
                    ><Smile size={12} /></button>
                    {reactionPickerCommentId === comment.id && (
                      <div style={{ position: 'absolute', bottom: '100%', left: 0, marginBottom: '0.2rem', background: 'rgba(20,15,35,0.98)', border: '1px solid rgba(147,51,234,0.5)', borderRadius: '0.5rem', padding: '0.3rem', display: 'flex', gap: '0.2rem', zIndex: 100, boxShadow: '0 4px 16px rgba(0,0,0,0.5)' }}>
                        {REACTION_EMOJIS.map(emoji => (
                          <button key={emoji} onClick={() => handleCommentReaction(comment.id, emoji)} style={{ background: 'none', border: 'none', fontSize: '1rem', cursor: 'pointer', padding: '0.15rem', borderRadius: '4px', transition: 'transform 0.15s' }} onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.3)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>{emoji}</button>
                        ))}
                      </div>
                    )}
                  </div>

                  {(() => {
                    const currentUser = JSON.parse(localStorage.getItem('beatseek_user') || '{}');
                    const isAuthor = currentUser.id === comment.author_id || currentUser.user_id === comment.author_id;
                    return isAuthor ? (
                      <>
                        <button
                          onClick={() => { setEditingCommentId(comment.id); setEditCommentContent(comment.content); }}
                          style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.4)', padding: '0.15rem 0.35rem', borderRadius: '0.3rem', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'all 0.2s' }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = '#00ffff'; e.currentTarget.style.borderColor = 'rgba(0,255,255,0.4)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; }}
                        ><Pencil size={12} /></button>
                        <button
                          onClick={() => handleDeleteComment(comment.id)}
                          style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.4)', padding: '0.15rem 0.35rem', borderRadius: '0.3rem', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'all 0.2s' }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = '#ff4444'; e.currentTarget.style.borderColor = 'rgba(255,0,0,0.4)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; }}
                        ><Trash2 size={12} /></button>
                      </>
                    ) : null;
                  })()}
                </div>
              </div>
            ))}
            {postComments.length === 0 && (
              <p style={{ color: 'rgba(255, 255, 255, 0.4)', textAlign: 'center', padding: '1rem' }}>
                No comments yet. Be the first!
              </p>
            )}
          </div>
        </div>
        </>
        )}
      </div>
    );
  }

  return (
    <div className="producer-forum-tab">
      {/* Category Tabs with New Post Button */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          {forumCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              style={{
                padding: '0.75rem 1.25rem',
                borderRadius: '10px',
                border: activeCategory === cat.id ? '1px solid #9333ea' : '1px solid rgba(147, 51, 234, 0.3)',
                background: activeCategory === cat.id ? 'rgba(147, 51, 234, 0.3)' : 'rgba(30, 20, 50, 0.5)',
                color: activeCategory === cat.id ? '#fbbf24' : 'rgba(255,255,255,0.7)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                fontSize: '0.875rem'
              }}
            >
              {cat.icon} {cat.name}
            </button>
          ))}
        </div>
        
        {/* New Post Button */}
        <button
          onClick={handleNewPost}
          style={{
            padding: '0.75rem 1.25rem',
            borderRadius: '10px',
            border: '1px solid #fbbf24',
            background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(147, 51, 234, 0.2))',
            color: '#fbbf24',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            fontSize: '0.875rem',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, rgba(251, 191, 36, 0.4), rgba(147, 51, 234, 0.4))';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(147, 51, 234, 0.2))';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <Plus size={16} /> New Post
        </button>
      </div>

      {/* Posts List */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: '32px', height: '32px', border: '3px solid rgba(147,51,234,0.3)', borderTopColor: '#9333ea', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }} />
            <p style={{ color: 'rgba(255,255,255,0.5)' }}>Loading posts...</p>
          </div>
        </div>
      ) : posts.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {posts.map((post) => (
            <div
              key={post.id}
              style={{
                background: 'rgba(30, 20, 50, 0.5)',
                border: '1px solid rgba(147, 51, 234, 0.2)',
                borderRadius: '12px',
                padding: '1.25rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(147, 51, 234, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(147, 51, 234, 0.2)';
              }}
            >
              {/* Title - Inline Edit or Display */}
              {editingPostId === post.id ? (
                <div onClick={(e) => e.stopPropagation()} style={{ marginBottom: '0.75rem' }}>
                  <input
                    data-testid={`edit-title-${post.id}`}
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      marginBottom: '0.5rem',
                      background: 'rgba(0, 0, 0, 0.4)',
                      border: '1px solid rgba(147, 51, 234, 0.5)',
                      borderRadius: '0.4rem',
                      color: '#fff',
                      fontSize: '1rem',
                      fontWeight: '600',
                      outline: 'none'
                    }}
                  />
                  <textarea
                    data-testid={`edit-content-${post.id}`}
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      background: 'rgba(0, 0, 0, 0.4)',
                      border: '1px solid rgba(147, 51, 234, 0.5)',
                      borderRadius: '0.4rem',
                      color: '#fff',
                      fontSize: '0.85rem',
                      outline: 'none',
                      resize: 'vertical'
                    }}
                  />
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <button
                      data-testid={`save-edit-${post.id}`}
                      onClick={(e) => handleEditPost(post.id, e)}
                      style={{
                        background: 'rgba(0, 255, 0, 0.2)',
                        border: '1px solid rgba(0, 255, 0, 0.5)',
                        borderRadius: '0.4rem',
                        color: '#00ff00',
                        padding: '0.3rem 0.75rem',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}
                    >
                      <Check size={12} /> Save
                    </button>
                    <button
                      data-testid={`cancel-edit-${post.id}`}
                      onClick={(e) => cancelEditing(e)}
                      style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        borderRadius: '0.4rem',
                        color: 'rgba(255, 255, 255, 0.7)',
                        padding: '0.3rem 0.75rem',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}
                    >
                      <X size={12} /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <h4 style={{ color: '#fff', fontSize: '1rem', fontWeight: '600', marginBottom: '0.75rem' }}>
                  {post.title}
                </h4>
              )}

              {/* Existing Reactions Display */}
              {post.reactions && post.reactions.length > 0 && (
                <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                  {post.reactions.map((reaction, rIdx) => (
                    <span
                      key={rIdx}
                      onClick={(e) => handleReaction(post.id, reaction.emoji, e)}
                      style={{
                        background: 'rgba(147, 51, 234, 0.15)',
                        border: '1px solid rgba(147, 51, 234, 0.3)',
                        borderRadius: '1rem',
                        padding: '0.15rem 0.5rem',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      {reaction.emoji} {reaction.count}
                    </span>
                  ))}
                </div>
              )}

              {/* Stats & Actions row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <span>by {post.author_name || post.author}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <MessageCircle size={14} /> {post.replies_count || post.replies || 0}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <ThumbsUp size={14} /> {post.likes_count || post.likes || 0}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Clock size={14} /> {formatDate(post.created_at) || post.time}
                </span>

                {/* Reaction picker */}
                <div style={{ position: 'relative' }}>
                  <button
                    data-testid={`reaction-btn-${post.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setReactionPickerPostId(reactionPickerPostId === post.id ? null : post.id);
                    }}
                    style={{
                      background: 'none',
                      border: '1px solid rgba(255,255,255,0.2)',
                      color: 'rgba(255,255,255,0.5)',
                      padding: '0.2rem 0.4rem',
                      borderRadius: '0.4rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#9333ea'; e.currentTarget.style.borderColor = 'rgba(147,51,234,0.5)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; }}
                  >
                    <Smile size={14} />
                  </button>
                  {reactionPickerPostId === post.id && (
                    <div
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: 'absolute',
                        bottom: '100%',
                        left: 0,
                        marginBottom: '0.25rem',
                        background: 'rgba(20, 15, 35, 0.98)',
                        border: '1px solid rgba(147, 51, 234, 0.5)',
                        borderRadius: '0.5rem',
                        padding: '0.4rem',
                        display: 'flex',
                        gap: '0.25rem',
                        zIndex: 100,
                        boxShadow: '0 4px 16px rgba(0,0,0,0.5)'
                      }}
                    >
                      {REACTION_EMOJIS.map(emoji => (
                        <button
                          key={emoji}
                          onClick={(e) => handleReaction(post.id, emoji, e)}
                          style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '1.1rem',
                            cursor: 'pointer',
                            padding: '0.2rem',
                            borderRadius: '4px',
                            transition: 'transform 0.15s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.3)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Author-only: Edit & Delete */}
                {(() => {
                  const currentUser = JSON.parse(localStorage.getItem('beatseek_user') || '{}');
                  const isAuthor = currentUser.id === post.author_id || currentUser.user_id === post.author_id;
                  return isAuthor ? (
                    <>
                      <button
                        data-testid={`edit-btn-${post.id}`}
                        onClick={(e) => startEditing(post, e)}
                        style={{
                          background: 'none',
                          border: '1px solid rgba(255,255,255,0.2)',
                          color: 'rgba(255,255,255,0.5)',
                          padding: '0.2rem 0.4rem',
                          borderRadius: '0.4rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = '#00ffff'; e.currentTarget.style.borderColor = 'rgba(0,255,255,0.4)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; }}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        data-testid={`delete-btn-${post.id}`}
                        onClick={(e) => handleDeletePost(post.id, e)}
                        style={{
                          background: 'none',
                          border: '1px solid rgba(255,255,255,0.2)',
                          color: 'rgba(255,255,255,0.5)',
                          padding: '0.2rem 0.4rem',
                          borderRadius: '0.4rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = '#ff4444'; e.currentTarget.style.borderColor = 'rgba(255,0,0,0.4)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </>
                  ) : null;
                })()}
                </div>

                {/* View Comments & View Post buttons - bottom right */}
                <button
                  data-testid={`view-comments-${post.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDetailViewMode('comments');
                    setSelectedPost(post);
                    loadPostComments(post.id);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'rgba(251, 191, 36, 0.8)',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    padding: '0.3rem 0',
                    transition: 'color 0.2s',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#fbbf24'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(251, 191, 36, 0.8)'}
                >
                  View Comments
                </button>
                <button
                  data-testid={`view-post-${post.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDetailViewMode('post');
                    setSelectedPost(post);
                    loadPostComments(post.id);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'rgba(147, 51, 234, 0.8)',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    padding: '0.3rem 0',
                    transition: 'color 0.2s',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#9333ea'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(147, 51, 234, 0.8)'}
                >
                  View Post →
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <Users size={48} style={{ color: 'rgba(255,255,255,0.3)', marginBottom: '1rem' }} />
          <p style={{ color: 'rgba(255,255,255,0.5)' }}>No posts in this category yet</p>
          <button
            onClick={handleNewPost}
            style={{
              marginTop: '1rem',
              padding: '0.75rem 1.5rem',
              borderRadius: '10px',
              border: '1px solid #fbbf24',
              background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(147, 51, 234, 0.2))',
              color: '#fbbf24',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            Be the first to post!
          </button>
        </div>
      )}

      {/* New Post Modal */}
      {showNewPostModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(20, 10, 35, 0.98), rgba(30, 15, 50, 0.98))',
            border: '2px solid rgba(147, 51, 234, 0.4)',
            borderRadius: '16px',
            padding: '2rem',
            width: '100%',
            maxWidth: '600px',
            maxHeight: '85vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ color: '#fbbf24', fontSize: '1.25rem', fontWeight: '600' }}>
                New {getCategoryInfo().name} Post
              </h3>
              <button
                onClick={resetForm}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'rgba(255,255,255,0.6)',
                  cursor: 'pointer',
                  padding: '0.5rem'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                  Title
                </label>
                <input
                  type="text"
                  value={newPostTitle}
                  onChange={(e) => setNewPostTitle(e.target.value)}
                  placeholder={getCategoryInfo().titlePlaceholder}
                  maxLength={200}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    background: 'rgba(30, 20, 50, 0.5)',
                    border: '1px solid rgba(147, 51, 234, 0.3)',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '0.9rem'
                  }}
                />
              </div>

              <div>
                <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                  Content
                </label>
                <div style={{ position: 'relative' }}>
                  <textarea
                    value={newPostContent}
                    onChange={(e) => setNewPostContent(e.target.value)}
                    placeholder={getCategoryInfo().contentPlaceholder}
                    maxLength={2000}
                    rows={5}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      paddingRight: '3rem',
                      background: 'rgba(30, 20, 50, 0.5)',
                      border: '1px solid rgba(147, 51, 234, 0.3)',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '0.9rem',
                      resize: 'vertical',
                      minHeight: '120px'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowEmotePicker(!showEmotePicker)}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '0.75rem',
                      background: 'transparent',
                      border: 'none',
                      color: showEmotePicker ? '#fbbf24' : 'rgba(255,255,255,0.5)',
                      cursor: 'pointer',
                      padding: '0.25rem'
                    }}
                  >
                    <Smile size={20} />
                  </button>
                  <EmotePicker 
                    isOpen={showEmotePicker} 
                    onEmoteSelect={handleEmoteSelect} 
                    onClose={() => setShowEmotePicker(false)} 
                  />
                </div>
              </div>

              {/* Track Preview Section - Only for feedback category */}
              {activeCategory === 'feedback' && (
                <div style={{
                  background: 'rgba(147, 51, 234, 0.1)',
                  border: '1px solid rgba(147, 51, 234, 0.3)',
                  borderRadius: '12px',
                  padding: '1rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Music size={18} style={{ color: '#fbbf24' }} />
                      <span style={{ color: '#fff', fontWeight: '600' }}>Track Preview</span>
                      <span style={{ color: '#ef4444', fontSize: '0.75rem' }}>*Required</span>
                    </div>
                    <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>
                      {userQuota.current}/{userQuota.max} active previews
                    </span>
                  </div>

                  {audioUploadId ? (
                    <div style={{
                      background: 'rgba(34, 197, 94, 0.1)',
                      border: '1px solid rgba(34, 197, 94, 0.3)',
                      borderRadius: '8px',
                      padding: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem'
                    }}>
                      <div style={{ color: '#4ade80' }}>✓</div>
                      <span style={{ color: '#4ade80', fontSize: '0.875rem' }}>Track preview added</span>
                      <button
                        onClick={() => {
                          setAudioUploadId(null);
                          setAudioFile(null);
                          setAudioPreviewUrl(null);
                        }}
                        style={{
                          marginLeft: 'auto',
                          background: 'transparent',
                          border: 'none',
                          color: 'rgba(255,255,255,0.5)',
                          cursor: 'pointer'
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowAudioUpload(true)}
                      disabled={userQuota.current >= userQuota.max}
                      style={{
                        width: '100%',
                        padding: '1rem',
                        borderRadius: '8px',
                        border: '2px dashed rgba(147, 51, 234, 0.4)',
                        background: 'transparent',
                        color: userQuota.current >= userQuota.max ? 'rgba(255,255,255,0.3)' : '#c4b5fd',
                        cursor: userQuota.current >= userQuota.max ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        fontSize: '0.875rem'
                      }}
                    >
                      <Upload size={18} />
                      {userQuota.current >= userQuota.max ? 'Max previews reached' : 'Add Track Preview (45-sec clip)'}
                    </button>
                  )}

                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem', marginTop: '0.5rem' }}>
                    🎵 Upload audio • Select 45-sec clip • Expires after 7 days • Max 2 active previews
                  </p>
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button
                  onClick={resetForm}
                  disabled={submitting}
                  style={{
                    padding: '0.75rem 1.5rem',
                    borderRadius: '10px',
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'transparent',
                    color: 'rgba(255,255,255,0.7)',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitNewPost}
                  disabled={submitting || !newPostTitle.trim() || !newPostContent.trim() || (activeCategory === 'feedback' && !audioUploadId)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    borderRadius: '10px',
                    border: 'none',
                    background: (submitting || !newPostTitle.trim() || !newPostContent.trim() || (activeCategory === 'feedback' && !audioUploadId))
                      ? 'rgba(147, 51, 234, 0.3)' 
                      : 'linear-gradient(135deg, #9333ea, #fbbf24)',
                    color: '#fff',
                    cursor: (submitting || !newPostTitle.trim() || !newPostContent.trim() || (activeCategory === 'feedback' && !audioUploadId)) ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '600'
                  }}
                >
                  {submitting ? 'Publishing...' : 'Publish Post'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Audio Upload Modal */}
      {showAudioUpload && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.85)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
          padding: '1rem'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(20, 10, 35, 0.98), rgba(30, 15, 50, 0.98))',
            border: '2px solid rgba(147, 51, 234, 0.4)',
            borderRadius: '16px',
            padding: '2rem',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '85vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ color: '#fbbf24', fontSize: '1.25rem', fontWeight: '600' }}>Add Track Preview</h3>
              <button
                onClick={() => {
                  setShowAudioUpload(false);
                  setAudioFile(null);
                  setAudioPreviewUrl(null);
                  setTermsAccepted(false);
                }}
                style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer' }}
              >
                <X size={24} />
              </button>
            </div>

                {/* 7-Day Warning */}
                <div style={{
                  background: 'rgba(251, 191, 36, 0.15)',
                  border: '1px solid rgba(251, 191, 36, 0.4)',
                  borderRadius: '10px',
                  padding: '1rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{ fontSize: '1.25rem' }}>⚠️</span>
                    <div>
                      <p style={{ color: '#fbbf24', fontWeight: '600', fontSize: '0.9rem' }}>7-Day Preview Policy</p>
                      <p style={{ color: 'rgba(251, 191, 36, 0.8)', fontSize: '0.8rem' }}>
                        Share your track for up to 7 days for record labels and A&R reps to review
                      </p>
                    </div>
                  </div>
                </div>

            {/* File Upload */}
            {!audioFile ? (
              <div style={{
                border: '2px dashed rgba(147, 51, 234, 0.4)',
                borderRadius: '12px',
                padding: '2rem',
                textAlign: 'center',
                marginBottom: '1rem'
              }}>
                <Music size={40} style={{ color: 'rgba(255,255,255,0.4)', marginBottom: '1rem' }} />
                <p style={{ color: '#fff', fontWeight: '600', marginBottom: '0.5rem' }}>Drop your audio file here</p>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', marginBottom: '1rem' }}>
                  MP3, WAV, M4A, AAC • Max 10MB • Min 45 seconds
                </p>
                <input
                  type="file"
                  accept=".mp3,.wav,.m4a,.aac,audio/*"
                  onChange={handleAudioFileSelect}
                  id="audio-upload-mini"
                  style={{ display: 'none' }}
                />
                <label
                  htmlFor="audio-upload-mini"
                  style={{
                    display: 'inline-block',
                    padding: '0.75rem 1.5rem',
                    background: 'linear-gradient(135deg, #9333ea, #7c3aed)',
                    color: '#fff',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  Choose File
                </label>
              </div>
            ) : (
              <div style={{
                background: 'rgba(30, 20, 50, 0.5)',
                border: '1px solid rgba(147, 51, 234, 0.3)',
                borderRadius: '12px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <span style={{ color: '#fff', fontWeight: '600', fontSize: '0.9rem' }}>{audioFile.name}</span>
                  <button
                    onClick={() => {
                      setAudioFile(null);
                      setAudioPreviewUrl(null);
                      setAudioDuration(0);
                    }}
                    style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer' }}
                  >
                    <X size={16} />
                  </button>
                </div>

                {audioPreviewUrl && (
                  <div>
                    <audio
                      ref={audioPlayerRef}
                      src={audioPreviewUrl}
                      onLoadedMetadata={(e) => {
                        setAudioDuration(e.target.duration);
                        setClipEndTime(Math.min(45, e.target.duration));
                      }}
                      controls
                      style={{ width: '100%', marginBottom: '1rem' }}
                    />

                    {audioDuration > 0 && (
                      <div style={{
                        background: 'rgba(147, 51, 234, 0.1)',
                        borderRadius: '8px',
                        padding: '0.75rem'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                          <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>Select 45-second clip:</span>
                          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem' }}>Total: {formatTime(audioDuration)}</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max={Math.max(0, audioDuration - 45)}
                          value={clipStartTime}
                          onChange={(e) => {
                            const start = parseFloat(e.target.value);
                            setClipStartTime(start);
                            setClipEndTime(Math.min(start + 45, audioDuration));
                          }}
                          style={{ width: '100%', marginBottom: '0.5rem' }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: '#4ade80', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                            🎵 {formatTime(clipStartTime)} → {formatTime(clipEndTime)} (45s)
                          </span>
                          <button
                            onClick={playClipPreview}
                            style={{
                              padding: '0.4rem 0.75rem',
                              background: '#9333ea',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.75rem',
                              cursor: 'pointer'
                            }}
                          >
                            ▶ Preview
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', marginTop: '0.5rem' }}>
                  Size: {(audioFile.size / (1024 * 1024)).toFixed(2)} MB
                </p>
              </div>
            )}

            {/* Terms */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <input
                type="checkbox"
                id="terms-mini"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                style={{ marginTop: '3px' }}
              />
              <label htmlFor="terms-mini" style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>
                I understand my track preview will expire after 7 days. Maximum 2 active track previews allowed.
              </label>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowAudioUpload(false);
                  setAudioFile(null);
                  setAudioPreviewUrl(null);
                  setTermsAccepted(false);
                }}
                style={{
                  padding: '0.75rem 1.25rem',
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  color: 'rgba(255,255,255,0.7)',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAudioUpload}
                disabled={audioUploading || !audioFile || !termsAccepted}
                style={{
                  padding: '0.75rem 1.25rem',
                  background: (audioUploading || !audioFile || !termsAccepted) ? 'rgba(147, 51, 234, 0.3)' : 'linear-gradient(135deg, #9333ea, #fbbf24)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  cursor: (audioUploading || !audioFile || !termsAccepted) ? 'not-allowed' : 'pointer',
                  fontWeight: '600'
                }}
              >
                {audioUploading ? 'Uploading...' : 'Add Track Preview'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ProducerForumTab;
